import React, {
  useRef,
  useEffect,
  useMemo,
  Fragment,
  forwardRef,
  useState,
} from "react";
import { useServiceStore } from "store";
import { GRAINS, pixelPerInch } from "constants/appConstant";
import Wrapper from "./headwall-components/wrapper";
import { HorizontalRuler, HospitalBed } from "components";
import { alterImageUrl } from "config/calculations/calculation";

const Eloquence = forwardRef((props, ref) => {
  const {
    values,
    ruler,
    handleDragStart,
    handleonDragOver,
    handleDrop,
    handleDragEnter,
    dragEntered,
    wallLength,
    service,
    clickArrowRight,
    clickArrowLeft,
    showBedGraphic,
  } = props;
  const { serv, view_only, setService } = useServiceStore();
  const heightRef = useRef(null);
  const wrapperRef = useRef(null);
  const [customEvent, setCustomEvent] = useState(null);
  const [railInd, setRailInd] = useState(0);

  const {
    headwalls,
    width,
    ceiling_height,
    model,
    sub_model,
    accessory_rails,
  } = values;

  //adding some extra pixels in outer div to handle scroll issue while for left and right chase
  const widthCaluclation = useMemo(() => {
    return Math.round((width.ft * 12 + width.in) / pixelPerInch) + 500;
  }, [width]);

  //chase width calculation
  const railWidth = useMemo(() => {
    if (sub_model === "standard")
      return (
        Math.round((width.ft * 12 + width.in) / pixelPerInch) -
        83.7071428571 -
        17.8571428571
      );
    //4.6876 borders area
    else
      return (
        Math.round((width.ft * 12 + width.in) / pixelPerInch) - 22.3214285714 //1.25 border area
      ); //4.6876 borders area
  }, [width, sub_model]);

  const wallWidth = useMemo(() => {
    return Math.round((width.ft * 12 + width.in) / pixelPerInch);
  }, [width]);

  const serviceWidth = (ser) => {
    if (ser.category === "Medical Gas Outlet")
      return (ser.width + 2.375) / pixelPerInch; //2.375 for side margins
    else return (ser.width + 0.125) / pixelPerInch;
  };

  const servicePosition = (ser) => {
    return `translate(${ser.positions?.x}px, ${0}px)`;
  };

  const generateStyle = (ser) => {
    const styleObj = {
      cursor: "grab",
      background:
        ser === serv
          ? view_only
            ? "rgba(207, 35, 35, 0.2)"
            : "transparent"
          : "transparent",
    };
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor);
    if (isSafari) {
      styleObj.left = ser.positions.x;
      styleObj.top = ser?.is_slide ? "80px" : "0px";
    } else styleObj.transform = servicePosition(ser);
    return styleObj;
  };

  const wallHeight = useMemo(() => {
    return Math.round(
      (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch
    );
  }, [ceiling_height]);

  useEffect(() => {
    //to adjust scale for axiom
    // Select all elements with the class 'scale'
    if (ruler) {
      const firstElements = document.querySelector(".first");
      const nextElements = document.querySelectorAll(".next");

      // Update styles for 'first' elements
      firstElements.style.maxWidth = "220.285714285px";

      nextElements.forEach((element) => {
        element.style.maxWidth = "214.285714285px";
      });
      // Cleanup function to revert styles when component unmounts
      return () => {
        firstElements.style.maxWidth = "";
        nextElements.forEach((element) => {
          element.style.maxWidth = "";
        });
      };
    }
  }, [ruler, width]);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowUp":
        break;
      case "ArrowDown":
        break;
      case "ArrowLeft":
        view_only && clickArrowLeft(customEvent, serv, railInd, 0, "infinity");
        break;
      case "ArrowRight":
        view_only && clickArrowRight(customEvent, serv, railInd, 0, "infinity");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    let isDragging = false;

    const handleClickOutside = (e) => {
      const clickedInsideFlexItemsEnd = e.target.closest(
        ".flex.items-end".split(" ").join(".")
      );
      if (
        !isDragging &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        clickedInsideFlexItemsEnd == null
      ) {
        setService("", false);
      }
    };

    const handleDragStart = () => (isDragging = true);
    const handleDragEnd = () => (isDragging = false);

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside); // fixed
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside); // match above
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, [serv, view_only]);

  const rulerWidth = useMemo(() => {
    let totalFeet = 0;
    let totalInches = 0;

    totalFeet = width.ft;
    totalInches = width.in;

    return { ft: totalFeet, in: totalInches };
  }, [width]);

  return (
    <>
      <Wrapper
        ruler={ruler}
        width={widthCaluclation}
        wallHeight={wallHeight}
        wallLength={wallLength}
        railWidth={railWidth}
        ceiling_height={ceiling_height}
        model={model}
        ref={ref}
      >
        <div className={`h-full flex justify-center `}>
          <div
            id="eloquence-board"
            className="h-full flex relative justify-center"
          >
            <div
              className={`flex flex-col h-full relative justify-end `}
              ref={heightRef}
              style={{
                width: wallWidth,
                height:
                  (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch,
              }}
            >
              <>
                <div
                  id="r_eloquence"
                  className="rail_center relative"
                  onDrop={(e) => handleDrop(e, "rail")}
                  onDragOver={(e) => handleonDragOver(e, "rail")}
                  onDragEnter={(e) => handleDragEnter(e)}
                >
                  {accessory_rails["direct"]?.map((acc, i) => (
                    <div
                      draggable
                      key={i}
                      className="absolute flex justify-center z-10"
                      style={{
                        left: acc.positions?.x,
                        top: acc.positions?.y,
                        cursor: "grab",
                      }}
                      onDragStart={(e) => handleDragStart(acc, e)}
                      onDragEnter={(e) => handleDragEnter(e)}
                      onDrop={(e) => handleDrop(e, "rail")}
                      onDragOver={(e) => handleonDragOver(e, "rail")}
                    >
                      <div
                        draggable={"false"}
                        style={{
                          width: acc.width / pixelPerInch,
                          backgroundImage: `url(${alterImageUrl(
                            acc.image_url
                          )})`,
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          pointerEvents: "none",
                          height: acc.height / pixelPerInch,
                        }}
                        onDragEnter={(e) => e.stopPropagation()}
                      />
                    </div>
                  ))}
                  <div
                    className={`w-max ${
                      sub_model === "standard"
                        ? "equolence_headwall_standard"
                        : "equolence_headwall_r grid gap-[10px]"
                    }`}
                  >
                    {headwalls?.map((rail, railIndex) => (
                      <Fragment key={railIndex}>
                        <div
                          className={`${
                            sub_model === "standard"
                              ? "equolence_headwall_standard_inner"
                              : "equolence_headwall_r_inner"
                          }`}
                        >
                          <div
                            id="equolence_rail"
                            className={`relative h-[86.4964285714px] flex w-full`}
                            style={{
                              backgroundColor: rail?.color,
                              width: railWidth,
                              backgroundImage: `url(${
                                GRAINS[rail?.background]
                              })`,
                            }}
                            onDrop={(e) => handleDrop(e, "rail", railIndex)}
                            onDragOver={(e) => handleonDragOver(e, "rail")}
                          >
                            {rail.services.map((ser, serviceIndex) => (
                              <Fragment key={serviceIndex}>
                                <div
                                  draggable={true}
                                  draggableid={ser._id}
                                  className={`service_main absolute py-1 flex justify-center h-full items-center z-10 ${
                                    dragEntered &&
                                    !service.service.is_accessory_rail &&
                                    !service.service.is_accessory
                                      ? "non_dropable "
                                      : ""
                                  }`}
                                  style={generateStyle(ser)}
                                  onDragStart={(e) =>
                                    handleDragStart(
                                      ser,
                                      e,
                                      railIndex,
                                      serviceIndex
                                    )
                                  }
                                  onDragEnter={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    const rect =
                                      e.target.getBoundingClientRect();
                                    setCustomEvent(e);
                                    setService(ser, true);
                                    setRailInd(railIndex);
                                  }}
                                >
                                  <div
                                    draggable={"false"}
                                    className="service_img pointer-events-none"
                                    style={{
                                      width: serviceWidth(ser),
                                      backgroundImage: `url(${alterImageUrl(
                                        ser.image_url
                                      )})`,
                                      backgroundSize: "contain",
                                      backgroundRepeat: "no-repeat",
                                      backgroundPosition: "center",
                                    }}
                                    onDragEnter={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </Fragment>
                            ))}
                          </div>
                        </div>
                      </Fragment>
                    ))}
                  </div>
                </div>
                {ruler && (
                  <HorizontalRuler
                    class_name={"w-full bottom-[14px] z-50"}
                    width={rulerWidth}
                  />
                )}
              </>
            </div>
          </div>
        </div>
        {showBedGraphic && <HospitalBed />}
      </Wrapper>
    </>
  );
});

export default Eloquence;
